import type { ConnectorForSettings } from "../../../http/deckDesigner/types/connectors";
import type { SecondaryItem } from "../../../http/deckDesigner/types/secondaryItems";
import type { SubstructureForSettings } from "../../../http/deckDesigner/types/substructures";
import { SUPPORT_FAMILY_CODE_GRANULO, SUPPORT_FAMILY_CODE_NAG } from "../../../http/deckDesigner/types/supportFamilies";
import type { SupportForSettings } from "../../../http/deckDesigner/types/supports";
import {
  getFootprintCoordinates,
  getSlopeDirectionDegrees,
  getUphillSpanMm,
  isSlopedTerrain,
  maxSlopeRiseMm,
  type DeckFootprint,
  type TerrainSlope,
} from "./deckOutline";

export const ceilToDecimals = (value: number, decimals: number): number => {
  const factor = 10 ** decimals;
  return Math.ceil(value * factor) / factor;
};

export const floorToDecimals = (value: number, decimals: number): number => {
  const factor = 10 ** decimals;
  return Math.floor(value * factor) / factor;
};

export const calculateValidSupportRange = (
  substructure: SubstructureForSettings | null,
  boardThickness: number | null,
  support: SupportForSettings | null,
  secondaryItem: SecondaryItem | null,
  connector: ConnectorForSettings | null,
  value: number | null,
) => {
  const secondaryItemThickness = secondaryItem?.thickness ?? 0;
  const minimumDeckHeightMm =
    (substructure?.height ?? 0) +
    secondaryItemThickness +
    (connector?.thickness ?? 0) +
    (boardThickness ?? 0) +
    (support?.minHeight ?? 0);
  const maximumDeckHeightMm =
    (substructure?.height ?? 0) +
    secondaryItemThickness +
    (connector?.thickness ?? 0) +
    (boardThickness ?? 0) +
    (support?.maxHeight ?? 0);
  const totalHeightMm = value == null ? null : value * 10;

  return { minimumDeckHeightMm, maximumDeckHeightMm, totalHeightMm };
};

const DEGREES_TO_RADIANS = Math.PI / 180;

// How much the sloped terrain rises (mm) at the deck's highest point - 0 on a flat terrain, without
// building the deck outline.
export const calculateMaxSlopeRiseMm = (footprint: DeckFootprint, terrainSlope: TerrainSlope): number =>
  isSlopedTerrain(terrainSlope) ? maxSlopeRiseMm(getFootprintCoordinates(footprint), terrainSlope) : 0;

// The inverse of calculateMaxSlopeRiseMm: the largest slope (degrees, floored to 2 decimals so it's
// never above the true bound) towards directionDegrees that keeps the deck's rise within
// allowedRiseMm, i.e. atan(allowedRiseMm / the deck's span along that direction). null without a
// direction, or when even a flat terrain isn't enough.
export const calculateMaxSlopeDegrees = (
  footprint: DeckFootprint,
  directionDegrees: number | null,
  allowedRiseMm: number,
): number | null => {
  if (directionDegrees == null || allowedRiseMm < 0) {
    return null;
  }

  const uphillSpanMm = getUphillSpanMm(getFootprintCoordinates(footprint), directionDegrees);

  return uphillSpanMm > 0 ? floorToDecimals(Math.atan(allowedRiseMm / uphillSpanMm) / DEGREES_TO_RADIANS, 2) : null;
};

// The slope to suggest for a support that can absorb at most allowedRiseMm of terrain rise: the
// current one if it already fits, otherwise the steepest one that does in the same direction (see
// calculateMaxSlopeDegrees) - the terrain keeps rising the same way, only less steep. maxRiseMm, here
// and below: calculateMaxSlopeRiseMm(footprint, terrainSlope), when the caller already has it.
export const calculateSuggestedSlope = (
  footprint: DeckFootprint,
  terrainSlope: TerrainSlope,
  allowedRiseMm: number,
  maxRiseMm?: number,
): number => {
  const currentSlope = terrainSlope.slopeDegrees ?? 0;

  if ((maxRiseMm ?? calculateMaxSlopeRiseMm(footprint, terrainSlope)) <= allowedRiseMm) {
    return currentSlope;
  }

  return Math.min(
    currentSlope,
    calculateMaxSlopeDegrees(
      footprint,
      getSlopeDirectionDegrees(terrainSlope.slopeArrow),
      Math.max(allowedRiseMm, 0),
    ) ?? 0,
  );
};

// A sloped terrain rises away from the deck's lowest vertex, where totalHeight (H) applies, so the
// support needed elsewhere on the deck is shorter than there - shortest at the deck's highest
// terrain point (calculateMaxSlopeRiseMm).
export const calculateWorstCaseSlopeSupportHeightMm = (
  substructure: SubstructureForSettings | null,
  boardThickness: number | null,
  support: SupportForSettings | null,
  secondaryItem: SecondaryItem | null,
  connector: ConnectorForSettings | null,
  totalHeight: number | null,
  footprint: DeckFootprint,
  terrainSlope: TerrainSlope,
  maxRiseMm?: number,
): number | null => {
  const { minimumDeckHeightMm, totalHeightMm } = calculateValidSupportRange(
    substructure,
    boardThickness,
    support,
    secondaryItem,
    connector,
    totalHeight,
  );

  if (totalHeightMm == null || footprint.base == null || footprint.height == null) {
    return null;
  }

  const fixedElementsHeightMm = minimumDeckHeightMm - (support?.minHeight ?? 0);
  const supportHeightBudgetMm = totalHeightMm - fixedElementsHeightMm;

  return supportHeightBudgetMm - (maxRiseMm ?? calculateMaxSlopeRiseMm(footprint, terrainSlope));
};

// The inverse of calculateWorstCaseSlopeSupportHeightMm: instead of checking one H value against a
// support, this returns the band of H (total height, in cm) that would make a support with the given
// min/max height valid under the current stack and slope - so the user can be told what H to pick.
// The lower bound already accounts for the slope (it's always at least as strict as the flat-terrain
// minimum, since the slope only ever subtracts more available height); the upper bound is unaffected
// by slope, since the lowest vertex (where H applies) is unaffected by it by definition.
export const calculateSlopeAdjustedSupportHeightRangeCm = (
  substructure: SubstructureForSettings | null,
  boardThickness: number | null,
  secondaryItem: SecondaryItem | null,
  connector: ConnectorForSettings | null,
  supportMinHeight: number,
  supportMaxHeight: number,
  footprint: DeckFootprint,
  terrainSlope: TerrainSlope,
  maxRiseMm?: number,
): { minTotalHeightCm: number; maxTotalHeightCm: number } => {
  const fixedElementsHeightMm =
    (substructure?.height ?? 0) + (secondaryItem?.thickness ?? 0) + (connector?.thickness ?? 0) + (boardThickness ?? 0);
  const minTotalHeightMm =
    fixedElementsHeightMm + supportMinHeight + (maxRiseMm ?? calculateMaxSlopeRiseMm(footprint, terrainSlope));
  const maxTotalHeightMm = fixedElementsHeightMm + supportMaxHeight;

  return { minTotalHeightCm: minTotalHeightMm / 10, maxTotalHeightCm: maxTotalHeightMm / 10 };
};

// NAG and GRANULO are stacked from whole units of minHeight, unlike SUP-S/M/L or JFA which are
// continuously adjustable within their range.
export const isStackableSupport = (support: SupportForSettings): boolean =>
  support.supportFamily.code === SUPPORT_FAMILY_CODE_NAG || support.supportFamily.code === SUPPORT_FAMILY_CODE_GRANULO;

// Everything a support height check depends on besides the support and the connector.
export type SupportHeightContext = {
  substructure: SubstructureForSettings | null;
  boardThickness: number | null;
  secondaryItem: SecondaryItem | null;
  totalHeight: number | null;
  footprint: DeckFootprint;
  terrainSlope: TerrainSlope;
  // calculateMaxSlopeRiseMm(footprint, terrainSlope), when already known - the same for every support.
  maxRiseMm?: number;
};

export type SupportHeightCheck = {
  disabled: boolean;
  // The flat-terrain checks only (the support's range, and whole units for the stackable families) -
  // tells the slope apart from the other reasons a support doesn't fit.
  disabledIgnoringSlope: boolean;
};

// Whether the support can't reach totalHeight with this connector in the stack. substructure and
// boardThickness are allowed to be null here - calculateValidSupportRange already treats each missing
// piece as contributing 0 to the stack height, matching the server's own calculation, so this
// recomputes against whatever is currently known.
export const getSupportHeightCheck = (
  support: SupportForSettings,
  connector: ConnectorForSettings | null,
  context: SupportHeightContext,
): SupportHeightCheck => {
  const { substructure, boardThickness, secondaryItem, totalHeight, footprint, terrainSlope } = context;
  const { minimumDeckHeightMm, maximumDeckHeightMm, totalHeightMm } = calculateValidSupportRange(
    substructure,
    boardThickness,
    support,
    secondaryItem,
    connector,
    totalHeight,
  );

  if (totalHeightMm == null) {
    return { disabled: false, disabledIgnoringSlope: false };
  }

  const isStackable = isStackableSupport(support);
  // The stackable families need the required support height to be an exact multiple of minHeight,
  // otherwise stacking would either fall short or overshoot the target.
  const disabledIgnoringSlope =
    !(minimumDeckHeightMm <= totalHeightMm && totalHeightMm <= maximumDeckHeightMm) ||
    (isStackable &&
      (support.minHeight <= 0 || (totalHeightMm - minimumDeckHeightMm + support.minHeight) % support.minHeight !== 0));

  if (disabledIgnoringSlope) {
    return { disabled: true, disabledIgnoringSlope };
  }

  // NAG and GRANULO are only offered on flat terrain: they stack whole units of minHeight, so on a
  // slope the required height changes continuously along the joist and can't be matched by whole units.
  if (isStackable && isSlopedTerrain(terrainSlope)) {
    return { disabled: true, disabledIgnoringSlope };
  }

  // A sloped terrain needs the support to go shorter than the flat-terrain minimum at the deck's
  // highest terrain point - independent of the stacking check above, and applies to every support
  // family, not just the stackable ones.
  const worstCaseSlopeHeightMm = calculateWorstCaseSlopeSupportHeightMm(
    substructure,
    boardThickness,
    support,
    secondaryItem,
    connector,
    totalHeight,
    footprint,
    terrainSlope,
    context.maxRiseMm,
  );

  return {
    disabled: worstCaseSlopeHeightMm != null && worstCaseSlopeHeightMm < support.minHeight,
    disabledIgnoringSlope,
  };
};
